import {
  Alert,
  Backdrop,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Icon,
  Radio,
  RadioGroup,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "../../../axios";
import usePageTitle from "../../pageTitle";

function PredictQuestionPage() {
  usePageTitle("Predict & Win");

  const { ref } = useParams();

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [question, setQuestion] = useState(null);
  const [promotion, setPromotion] = useState(null);
  const [options, setOptions] = useState([]);
  const [answers, setAnswers] = useState([]);
  const [prizes, setPrizes] = useState([]);

  const [participant, setParticipant] = useState(null);
  const [registrationOpen, setRegistrationOpen] = useState(false);
  const [participantName, setParticipantName] = useState("");
  const [participantEmail, setParticipantEmail] = useState("");
  const [participantPhone, setParticipantPhone] = useState("");

  const [selectedAnswers, setSelectedAnswers] = useState([]);
  const [selectedOptionAnswers, setSelectedOptionAnswers] = useState({});
  const [openAnswers, setOpenAnswers] = useState({});
  const [openAnswer, setOpenAnswer] = useState("");

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("error");
  const [successOpen, setSuccessOpen] = useState(false);

  const showMessage = (message, severity = "error") => {
    setSnackbarMessage(message || "Unable to complete request.");
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const getSavedParticipant = (promotionReference) => {
    try {
      const saved = JSON.parse(
        localStorage.getItem("laddar_predict_participant"),
      );

      if (
        saved &&
        saved.promotion === promotionReference &&
        saved.participant
      ) {
        return saved.participant;
      }
    } catch (error) {
      localStorage.removeItem("laddar_predict_participant");
    }

    return null;
  };

  const saveParticipant = (promotionReference, participantData) => {
    localStorage.setItem(
      "laddar_predict_participant",
      JSON.stringify({
        promotion: promotionReference,
        participant: participantData,
      }),
    );
  };

  useEffect(() => {
    const fetchQuestion = async () => {
      setLoading(true);

      try {
        const response = await axios.get(
          `web/tm/predict/question/${ref}/fetch.json`,
        );

        if (response.data.status !== "success") {
          showMessage(response.data.data?.message || "Question not found.");
          return;
        }

        const data = response.data.data;
        const questionData = data.question;

        setQuestion(questionData);
        setPromotion(data.promotion || questionData.promotion || null);
        setOptions(data.options || questionData.options || []);
        setAnswers(data.answers || questionData.answers || []);
        setPrizes(data.prizes || questionData.prizes || []);

        const promotionData = data.promotion || questionData.promotion;

        if (promotionData?.reference) {
          setParticipant(getSavedParticipant(promotionData.reference));
        }
      } catch (error) {
        showMessage(
          error.response?.data?.data?.message ||
            "Unable to load this question.",
        );
      } finally {
        setLoading(false);
      }
    };

    fetchQuestion();
  }, [ref]);

  const now = new Date();

  const opensAt = question?.opens_at ? new Date(question.opens_at) : null;

  const closesAt = question?.closes_at ? new Date(question.closes_at) : null;

  const predictionOpen =
    question?.status !== false &&
    Boolean(opensAt && closesAt) &&
    now >= opensAt &&
    now <= closesAt;

  const formatDate = (value) => {
    if (!value) return "—";

    const date = new Date(value);

    return Number.isNaN(date.getTime())
      ? value
      : date.toLocaleString([], {
          day: "numeric",
          month: "short",
          year: "numeric",
          hour: "numeric",
          minute: "2-digit",
        });
  };

  const getWindowStatus = () => {
    if (!opensAt || !closesAt) {
      return "Prediction schedule unavailable";
    }

    if (now < opensAt) return "Predictions have not opened yet";
    if (now > closesAt) return "The prediction window has closed";

    return "Predictions are open";
  };

  const questionType = question?.question_type;
  const answerType = question?.answer_type;
  const cardinality = question?.selection_cardinality;
  const isMultiple = cardinality === "MULTIPLE";

  const getAnswerInputType = () => {
    switch (question?.answer_value_type) {
      case "INTEGER":
      case "DECIMAL":
        return "number";
      case "DATE":
        return "date";
      case "DATETIME":
        return "datetime-local";
      default:
        return "text";
    }
  };

  const getInputStep = () => {
    if (question?.answer_value_type === "INTEGER") return 1;
    if (question?.answer_value_type === "DECIMAL") return "any";
    return undefined;
  };

  const updateDirectAnswer = (answerReference, checked) => {
    if (isMultiple) {
      setSelectedAnswers((previous) =>
        checked
          ? [...previous, answerReference]
          : previous.filter((item) => item !== answerReference),
      );
    } else {
      setSelectedAnswers([answerReference]);
    }
  };

  const updateOptionAnswer = (optionReference, answerReference, checked) => {
    setSelectedOptionAnswers((previous) => {
      const current = previous[optionReference];

      if (isMultiple) {
        const currentAnswers = Array.isArray(current) ? current : [];

        return {
          ...previous,
          [optionReference]: checked
            ? [...currentAnswers, answerReference]
            : currentAnswers.filter((item) => item !== answerReference),
        };
      }

      return {
        ...previous,
        [optionReference]: checked ? answerReference : "",
      };
    });
  };

  const buildAnswer = () => {
    if (questionType === "OPTIONS_ANSWERS") {
      if (answerType === "OPEN") {
        return Object.fromEntries(
          options.map((option) => [
            option.reference,
            openAnswers[option.reference] ?? "",
          ]),
        );
      }

      return selectedOptionAnswers;
    }

    if (answerType === "OPEN") return [openAnswer];

    return selectedAnswers;
  };

  const hasAnswer = (answer) => {
    if (Array.isArray(answer)) {
      return answer.some(
        (item) => item !== "" && item !== null && item !== undefined,
      );
    }

    if (answer && typeof answer === "object") {
      return Object.values(answer).some((value) =>
        Array.isArray(value)
          ? value.length > 0
          : value !== "" && value !== null && value !== undefined,
      );
    }

    return answer !== "" && answer !== null && answer !== undefined;
  };

  const registerParticipant = async () => {
    if (!promotion?.reference) {
      showMessage("Promotion information is unavailable.");
      return;
    }

    if (
      !participantName.trim() ||
      !participantEmail.trim() ||
      !participantPhone.trim()
    ) {
      showMessage("Name, email address and phone number are required.");
      return;
    }

    setSubmitting(true);

    try {
      const response = await axios.post("web/tm/predict/register.json", {
        reference: promotion.reference,
        name: participantName.trim(),
        email_address: participantEmail.trim(),
        phone_number: participantPhone.trim(),
      });

      if (response.data.status !== "success") {
        showMessage(
          response.data.data?.message || "Unable to register participant.",
        );
        return;
      }

      const participantData = response.data.data.participant;

      saveParticipant(promotion.reference, participantData);
      setParticipant(participantData);
      setRegistrationOpen(false);

      showMessage("Registration successful.", "success");
    } catch (error) {
      showMessage(
        error.response?.data?.data?.message ||
          "Unable to complete registration.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const submitPrediction = async () => {
    if (!participant) {
      setRegistrationOpen(true);
      return;
    }

    if (!predictionOpen) {
      showMessage("Predictions are not currently open.");
      return;
    }

    const answer = buildAnswer();

    if (!hasAnswer(answer)) {
      showMessage("Please provide your prediction.");
      return;
    }

    setSubmitting(true);

    try {
      const response = await axios.post(
        "web/tm/predict/question/predict.json",
        {
          question: question.reference,
          participant: participant.reference,
          answer,
        },
      );

      if (response.data.status !== "success") {
        showMessage(
          response.data.data?.message || "Unable to submit prediction.",
        );
        return;
      }

      setSuccessOpen(true);
    } catch (error) {
      showMessage(
        error.response?.data?.data?.message ||
          "Unable to submit your prediction.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const renderControlledAnswers = (option = null) => {
    const optionAnswers = answers.filter((answer) =>
      option
        ? answer.option?.reference === option.reference ||
          answer.option_id === option.reference
        : !answer.option && !answer.option_id,
    );

    if (isMultiple) {
      return (
        <Stack spacing={0.5}>
          {optionAnswers.map((answer) => {
            const checked = option
              ? (Array.isArray(selectedOptionAnswers[option.reference])
                  ? selectedOptionAnswers[option.reference]
                  : []
                ).includes(answer.reference)
              : selectedAnswers.includes(answer.reference);

            return (
              <Box
                key={answer.reference}
                onClick={() => {
                  if (!predictionOpen) return;

                  if (option) {
                    updateOptionAnswer(
                      option.reference,
                      answer.reference,
                      !checked,
                    );
                  } else {
                    updateDirectAnswer(answer.reference, !checked);
                  }
                }}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  p: 1,
                  border: "1px solid",
                  borderColor: checked ? "text.primary" : "divider",
                  borderRadius: 1.5,
                  bgcolor: checked ? "action.selected" : "background.paper",
                  cursor: predictionOpen ? "pointer" : "default",
                  transition: "all 0.15s ease",
                  "&:hover": predictionOpen
                    ? { borderColor: "text.primary", bgcolor: "action.hover" }
                    : {},
                }}
              >
                <Checkbox
                  checked={checked}
                  disabled={!predictionOpen}
                  size="small"
                  sx={{ mr: 1 }}
                />

                <Typography variant="body2" fontWeight={checked ? 600 : 400}>
                  {answer.label}
                </Typography>
              </Box>
            );
          })}
        </Stack>
      );
    }

    return (
      <RadioGroup
        value={
          option
            ? selectedOptionAnswers[option.reference] || ""
            : selectedAnswers[0] || ""
        }
        onChange={(event) =>
          option
            ? updateOptionAnswer(option.reference, event.target.value, true)
            : updateDirectAnswer(event.target.value, true)
        }
      >
        {optionAnswers.map((answer) => {
          const checked = option
            ? selectedOptionAnswers[option.reference] === answer.reference
            : selectedAnswers[0] === answer.reference;

          return (
            <Box
              key={answer.reference}
              sx={{
                display: "flex",
                alignItems: "center",
                mb: 0.75,
                px: 1,
                border: "1px solid",
                borderColor: checked ? "text.primary" : "divider",
                borderRadius: 1.5,
                bgcolor: checked ? "action.selected" : "background.paper",
                transition: "all 0.15s ease",
                "&:hover": predictionOpen
                  ? { borderColor: "text.primary", bgcolor: "action.hover" }
                  : {},
              }}
            >
              <FormControlLabel
                value={answer.reference}
                disabled={!predictionOpen}
                control={<Radio size="small" />}
                label={
                  <Typography variant="body2" fontWeight={checked ? 600 : 400}>
                    {answer.label}
                  </Typography>
                }
                sx={{ m: 0, flex: 1, py: 0.5 }}
              />
            </Box>
          );
        })}
      </RadioGroup>
    );
  };

  const renderOpenAnswer = (value, onChange) => (
    <TextField
      fullWidth
      size="medium"
      type={getAnswerInputType()}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      disabled={!predictionOpen}
      placeholder="Enter your prediction"
      slotProps={{
        htmlInput: {
          ...(getInputStep() !== undefined ? { step: getInputStep() } : {}),
        },
      }}
      sx={{
        mt: 1,
        "& .MuiOutlinedInput-root": {
          borderRadius: 1.5,
          bgcolor: "background.paper",
        },
      }}
    />
  );

  const cardSx = {
    border: "1px solid",
    borderColor: "divider",
    borderRadius: 2,
    boxShadow: "0 2px 12px rgba(0,0,0,0.035)",
    bgcolor: "background.paper",
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#f5f6f8",
        color: "#17191d",
        pb: { xs: 4, md: 7 },
      }}
    >
      <Backdrop
        open={loading}
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
          bgcolor: "rgba(18,20,24,0.45)",
        }}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <Box
        sx={{
          height: 8,
          background: "linear-gradient(90deg, #17191d 0%, #575b64 100%)",
        }}
      />

      <Box
        sx={{
          background:
            "radial-gradient(ellipse at 90% 0%, rgba(255,255,255,0.10), transparent 38%), linear-gradient(125deg, #17191d 0%, #292d35 100%)",
          color: "#fff",
          px: { xs: 2.5, md: 5 },
          pt: { xs: 4, md: 6 },
          pb: { xs: 5, md: 7 },
        }}
      >
        <Box sx={{ maxWidth: 900, mx: "auto" }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={2}
            sx={{ mb: 4 }}
          >
            <Stack direction="row" alignItems="center" spacing={1.25}>
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: 1.5,
                  display: "grid",
                  placeItems: "center",
                  bgcolor: "#fff",
                  color: "#17191d",
                  fontWeight: 800,
                  fontSize: 18,
                }}
              >
                <Icon>emoji_events</Icon>
              </Box>

              <Typography
                variant="subtitle1"
                sx={{ color: "#fff", fontWeight: 700, letterSpacing: 0.2 }}
              >
                Predict & Win
              </Typography>
            </Stack>

            {participant && (
              <Chip
                label={`Hi, ${participant.name?.split(" ")[0] || "there"}`}
                sx={{
                  color: "#fff",
                  bgcolor: "rgba(255,255,255,0.10)",
                  border: "1px solid rgba(255,255,255,0.18)",
                  fontWeight: 500,
                }}
              />
            )}
          </Stack>

          <Typography
            variant="overline"
            sx={{
              color: "rgba(255,255,255,0.65)",
              letterSpacing: 2,
              fontWeight: 600,
            }}
          >
            {promotion?.title || "Make your prediction"}
          </Typography>

          <Typography
            variant="h3"
            sx={{
              mt: 1,
              maxWidth: 760,
              fontWeight: 700,
              letterSpacing: { xs: -0.7, md: -1.4 },
              lineHeight: 1.15,
              fontSize: { xs: "2rem", sm: "2.6rem", md: "3rem" },
              overflowWrap: "anywhere",
            }}
          >
            {question?.question || question?.title || "Your next prediction"}
          </Typography>

          {/* <Typography
            variant="body1"
            sx={{
              mt: 2,
              maxWidth: 600,
              lineHeight: 1.8,
              color: "rgba(255,255,255,0.72)",
            }}
          >
            Make your call, trust your judgement, and submit your prediction
            before the window closes.
          </Typography> */}

          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ mt: 3, flexWrap: "wrap", rowGap: 1 }}
          >
            <Chip
              size="small"
              label={predictionOpen ? "PREDICTIONS OPEN" : "PREDICTIONS CLOSED"}
              icon={
                <Box
                  component="span"
                  sx={{
                    width: 7,
                    height: 7,
                    borderRadius: "50%",
                    bgcolor: predictionOpen ? "#7dd3a5" : "#f2a6a6",
                    ml: 1,
                  }}
                />
              }
              sx={{
                color: "#fff",
                bgcolor: predictionOpen
                  ? "rgba(47,155,98,0.2)"
                  : "rgba(200,70,70,0.2)",
                border: "1px solid",
                borderColor: predictionOpen
                  ? "rgba(125,211,165,0.35)"
                  : "rgba(242,166,166,0.35)",
                fontWeight: 700,
                letterSpacing: 0.5,
              }}
            />

            {question?.selection_cardinality && (
              <Chip
                size="small"
                label={
                  question.selection_cardinality === "MULTIPLE"
                    ? "Select multiple"
                    : "Select one"
                }
                sx={{
                  color: "rgba(255,255,255,0.85)",
                  bgcolor: "rgba(255,255,255,0.08)",
                  border: "1px solid rgba(255,255,255,0.16)",
                }}
              />
            )}
          </Stack>
        </Box>
      </Box>

      <Box
        sx={{
          maxWidth: 900,
          mx: "auto",
          px: { xs: 2, md: 4 },
          mt: { xs: -2.5, md: -3 },
        }}
      >
        {question && (
          <Stack spacing={2.5}>
            <Card sx={{ ...cardSx, p: { xs: 2, md: 2.75 } }}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2.5}
                divider={
                  <Divider
                    orientation="vertical"
                    flexItem
                    sx={{ display: { xs: "none", sm: "block" } }}
                  />
                }
              >
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight={700}
                    sx={{ letterSpacing: 0.7 }}
                  >
                    PREDICTION OPENS
                  </Typography>

                  <Typography
                    variant="body1"
                    fontWeight={600}
                    sx={{ mt: 0.75 }}
                  >
                    {formatDate(question.opens_at)}
                  </Typography>
                </Box>

                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight={700}
                    sx={{ letterSpacing: 0.7 }}
                  >
                    DEADLINE
                  </Typography>

                  <Typography
                    variant="body1"
                    fontWeight={600}
                    sx={{ mt: 0.75 }}
                  >
                    {formatDate(question.closes_at)}
                  </Typography>
                </Box>
              </Stack>

              <Divider sx={{ my: 2 }} />

              <Stack direction="row" alignItems="flex-start" spacing={1.25}>
                <Box
                  sx={{
                    width: 34,
                    height: 34,
                    borderRadius: 1.5,
                    flexShrink: 0,
                    display: "grid",
                    placeItems: "center",
                    bgcolor: predictionOpen ? "#e7f5ec" : "#f0f0f2",
                    color: predictionOpen ? "#267446" : "#555b65",
                    fontSize: 18,
                    fontWeight: 700,
                  }}
                >
                  {predictionOpen ? "✓" : "i"}
                </Box>

                <Box>
                  <Typography variant="body2" fontWeight={700}>
                    {getWindowStatus()}
                  </Typography>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 0.25, lineHeight: 1.6 }}
                  >
                    {predictionOpen
                      ? "Choose your answer below. You must submit before the deadline."
                      : now < opensAt
                        ? "Come back when the prediction window opens."
                        : "Submissions are no longer being accepted."}
                  </Typography>
                </Box>
              </Stack>
            </Card>

            {prizes.length > 0 && (
              <Card sx={{ ...cardSx, p: { xs: 2, md: 2.5 } }}>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <Box
                    sx={{
                      width: 42,
                      height: 42,
                      borderRadius: 1.5,
                      bgcolor: "#f2eee5",
                      color: "#7d5d26",
                      display: "grid",
                      placeItems: "center",
                      fontSize: 22,
                      flexShrink: 0,
                    }}
                  >
                    ★
                  </Box>

                  <Box>
                    <Typography variant="subtitle1" fontWeight={700}>
                      What's up for grabs?
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Prizes associated with this question
                    </Typography>
                  </Box>
                </Stack>

                <Stack spacing={1} sx={{ mt: 2 }}>
                  {prizes.map((prize, index) => (
                    <Box
                      key={prize.reference || `${prize.title}-${index}`}
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: 2,
                        py: 1,
                        borderTop: index === 0 ? "none" : "1px solid",
                        borderColor: "divider",
                      }}
                    >
                      <Typography variant="body2" fontWeight={600}>
                        {prize.title}
                      </Typography>

                      <Typography
                        variant="body2"
                        color="text.secondary"
                        textAlign="right"
                      >
                        {prize.reward?.title || prize.reward_type?.title || ""}
                        {(prize.reward?.value ?? prize.reward_value) != null
                          ? ` · ${prize.reward?.value ?? prize.reward_value}`
                          : ""}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </Card>
            )}

            <Box>
              <Typography
                variant="overline"
                color="text.secondary"
                sx={{ letterSpacing: 1.4, fontWeight: 700 }}
              >
                YOUR PREDICTION
              </Typography>

              <Typography
                variant="h5"
                fontWeight={700}
                sx={{ mt: 0.25, mb: 0.75, letterSpacing: -0.4 }}
              >
                What do you think?
              </Typography>

              {answerType === "CONTROLLED" ? (
                <Typography variant="body2" color="text.secondary">
                  {isMultiple
                    ? "Choose all that apply."
                    : "Select your prediction below."}
                </Typography>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Enter your prediction below.
                </Typography>
              )}
            </Box>

            {questionType === "OPTIONS_ANSWERS" ? (
              <Stack spacing={2}>
                {options.map((option, index) => (
                  <Card
                    key={option.reference}
                    sx={{ ...cardSx, p: { xs: 2, md: 2.5 } }}
                  >
                    <Stack
                      direction="row"
                      spacing={1.5}
                      alignItems="flex-start"
                    >
                      <Box
                        sx={{
                          width: 34,
                          height: 34,
                          flexShrink: 0,
                          display: "grid",
                          placeItems: "center",
                          borderRadius: 1.5,
                          bgcolor: "#f0f1f3",
                          color: "#343840",
                          fontWeight: 700,
                          fontSize: 14,
                        }}
                      >
                        {index + 1}
                      </Box>

                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="subtitle1" fontWeight={700}>
                          {option.title}
                        </Typography>

                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: "block", mt: 0.25, mb: 1.5 }}
                        >
                          {answerType === "CONTROLLED"
                            ? isMultiple
                              ? "Choose your answers"
                              : "Choose one answer"
                            : "Enter your prediction"}
                        </Typography>

                        {answerType === "CONTROLLED"
                          ? renderControlledAnswers(option)
                          : renderOpenAnswer(
                              openAnswers[option.reference] ?? "",
                              (value) =>
                                setOpenAnswers((previous) => ({
                                  ...previous,
                                  [option.reference]: value,
                                })),
                            )}
                      </Box>
                    </Stack>
                  </Card>
                ))}
              </Stack>
            ) : (
              <Card sx={{ ...cardSx, p: { xs: 2, md: 2.5 } }}>
                {answerType === "CONTROLLED" ? (
                  renderControlledAnswers()
                ) : (
                  <>
                    <Typography variant="subtitle1" fontWeight={700}>
                      Your answer
                    </Typography>

                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 0.5 }}
                    >
                      {question.answer_value_type === "INTEGER" ||
                      question.answer_value_type === "DECIMAL"
                        ? "Enter your numerical prediction."
                        : "Enter your prediction below."}
                    </Typography>

                    {renderOpenAnswer(openAnswer, setOpenAnswer)}
                  </>
                )}
              </Card>
            )}

            <Card sx={{ ...cardSx, p: { xs: 2, md: 2.5 } }}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                alignItems={{ xs: "stretch", sm: "center" }}
                justifyContent="space-between"
                spacing={2}
              >
                <Box>
                  <Typography variant="subtitle2" fontWeight={700}>
                    Ready to lock in your prediction?
                  </Typography>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 0.5 }}
                  >
                    {participant
                      ? `Submitting as ${participant.name}.`
                      : "Register your details to submit your prediction."}
                  </Typography>
                </Box>

                <Button
                  variant="contained"
                  size="large"
                  onClick={submitPrediction}
                  disabled={!predictionOpen || submitting}
                  endIcon={!submitting ? <span>→</span> : null}
                  sx={{
                    minWidth: { sm: 210 },
                    minHeight: 48,
                    borderRadius: 1.5,
                    px: 3,
                    bgcolor: "#202329",
                    color: "#fff",
                    fontWeight: 700,
                    textTransform: "none",
                    boxShadow: "none",
                    "&:hover": {
                      bgcolor: "#363a43",
                      boxShadow: "none",
                    },
                    "&.Mui-disabled": {
                      bgcolor: "#d7d9dd",
                      color: "#858991",
                    },
                  }}
                >
                  {submitting
                    ? "Submitting..."
                    : participant
                      ? "Submit prediction"
                      : "Register to predict"}
                </Button>
              </Stack>
            </Card>

            <Typography
              variant="caption"
              color="text.secondary"
              textAlign="center"
              sx={{ px: 2, lineHeight: 1.7 }}
            >
              Make sure your answers are correct before submitting. Duplicate
              submissions are not allowed.
            </Typography>
          </Stack>
        )}
      </Box>

      <Dialog
        open={registrationOpen}
        onClose={() => setRegistrationOpen(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: { borderRadius: 2, p: 0.5 },
        }}
      >
        <DialogTitle sx={{ pb: 0.5, fontWeight: 700 }}>
          Join and predict
        </DialogTitle>

        <DialogContent>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 2, lineHeight: 1.7 }}
          >
            Enter your details to participate in this prediction.
          </Typography>

          <Stack spacing={2}>
            <TextField
              label="Full name"
              value={participantName}
              onChange={(event) => setParticipantName(event.target.value)}
              autoComplete="name"
              required
              fullWidth
            />

            <TextField
              label="Email address"
              type="email"
              value={participantEmail}
              onChange={(event) => setParticipantEmail(event.target.value)}
              autoComplete="email"
              required
              fullWidth
            />

            <TextField
              label="Phone number"
              value={participantPhone}
              onChange={(event) => setParticipantPhone(event.target.value)}
              autoComplete="tel"
              required
              fullWidth
            />
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={() => setRegistrationOpen(false)}
            sx={{ color: "text.secondary", textTransform: "none" }}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            onClick={registerParticipant}
            disabled={submitting}
            sx={{
              bgcolor: "#202329",
              textTransform: "none",
              boxShadow: "none",
              "&:hover": { bgcolor: "#363a43", boxShadow: "none" },
            }}
          >
            {submitting ? "Registering..." : "Continue"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={successOpen}
        onClose={() => setSuccessOpen(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: { borderRadius: 2, textAlign: "center", p: 1 },
        }}
      >
        <DialogContent sx={{ pt: 4, pb: 2 }}>
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: "50%",
              bgcolor: "#e5f5eb",
              color: "#267446",
              display: "grid",
              placeItems: "center",
              mx: "auto",
              mb: 2,
              fontSize: 30,
              fontWeight: 700,
            }}
          >
            ✓
          </Box>

          <Typography variant="h5" fontWeight={700}>
            Prediction submitted!
          </Typography>

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 1, lineHeight: 1.8 }}
          >
            Your prediction has been received. Good luck!
          </Typography>
        </DialogContent>

        <DialogActions sx={{ justifyContent: "center", pb: 2.5 }}>
          <Button
            variant="contained"
            onClick={() => setSuccessOpen(false)}
            sx={{
              minWidth: 140,
              bgcolor: "#202329",
              textTransform: "none",
              boxShadow: "none",
              "&:hover": { bgcolor: "#363a43", boxShadow: "none" },
            }}
          >
            Done
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={5000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snackbarSeverity}
          onClose={() => setSnackbarOpen(false)}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default PredictQuestionPage;
